/**
 * The Calendar module's canonical occurrence model, and the four source mappers
 * that normalise into it.
 *
 * Calendar merges four authoritative ERPat sources — holidays, the signed-in
 * employee's leave applications, advisories targeted at them, and events they
 * can see — onto one timeline. Those four disagree about almost everything:
 * which key holds the date, whether an end is inclusive, whether a time part
 * exists, and what "status" means. This file is where that is resolved ONCE, so
 * the page renders one shape and never reasons about a raw payload.
 *
 * Three rules the whole module rests on:
 *
 *  1. **Civil dates stay civil.** Holidays, leave and advisories name a DAY, not
 *     an instant. They are carried as `YYYY-MM-DD` strings and only ever turned
 *     into a `Date` at LOCAL midnight. A date-only value dragged through UTC
 *     renders as the previous day for anyone west of Greenwich — which would
 *     file a first-of-month holiday under the month before.
 *  2. **Ends are stored EXCLUSIVE.** Every source hands back an inclusive last
 *     day; RFC 5545 and every overlap test want an exclusive one. Converting at
 *     the boundary (`endDateExclusive = lastDay + 1`) means the rest of the app
 *     can use the one correct visibility test — `start < rangeEnd && end >
 *     rangeStart` — instead of a containment check that silently drops a record
 *     spanning the whole window.
 *  3. **Nothing is inferred that the source did not say.** Holiday applicability,
 *     leave policy and workday impact are ERPat's to decide. Where a source
 *     cannot answer, `appliesToMe` is `null` and the UI says so, rather than
 *     showing a branch holiday as if it applied to everyone.
 */
import { asRecord, pick, type Json } from './http';
import { mapAdvisory, leaveStatusVariant, type LeaveStatusVariant } from './models';

// ── Civil-date primitives ────────────────────────────────────────────────────

/** A bare calendar date with no time part, e.g. `2026-09-14`. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The `YYYY-MM-DD` head of an API value, or '' when there isn't one.
 *
 * Handles both shapes this API uses for a date: the bare `"2026-09-14"` that
 * holidays / leave / advisories send, and the `"2026-09-14 14:00:00"` that
 * events send. Anything else (null, a number, a malformed string) is rejected
 * rather than guessed at.
 */
export function civilDatePart(value: unknown): string {
  if (typeof value !== 'string') return '';
  const head = value.trim().slice(0, 10);
  return DATE_ONLY_RE.test(head) ? head : '';
}

/** An ISO value that names an INSTANT — it carries `T` plus `Z` or an offset. */
const ZONED_INSTANT_RE = /[Tt].*(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/**
 * The LOCAL civil date of an API value — the day the reader is living in.
 *
 * Three shapes reach this app and they are NOT interchangeable:
 *
 *  - `"2026-09-14"` is already a civil date. Returned unchanged.
 *  - `"2026-09-14 22:00:00"` is a naive wall clock with no zone, which is how
 *    this API stores every timestamp. Its date part is the civil date.
 *  - `"2026-09-14T22:00:00.000Z"` is an INSTANT. Slicing its first ten
 *    characters hands back the UTC day, which for anyone far enough east or
 *    west is the wrong day for several hours out of every twenty-four — an
 *    advisory created at 10am in Manila would file under the previous day. It
 *    is resolved and asked which day it falls on HERE instead.
 */
export function civilDateOf(value: unknown): string {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : civilKeyOf(value);
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text) return '';
  if (ZONED_INSTANT_RE.test(text)) {
    const instant = new Date(text);
    return Number.isNaN(instant.getTime()) ? '' : civilKeyOf(instant);
  }
  return civilDatePart(text);
}

/**
 * Minutes past midnight for the time part of `"YYYY-MM-DD HH:MM[:SS]"`, or
 * `-1` when the value carries no time. Read as a WALL CLOCK: the events
 * endpoint stores a naive local time with no zone, so there is nothing to
 * convert from and converting would move it.
 */
export function civilMinutePart(value: unknown): number {
  if (typeof value !== 'string') return -1;
  const match = value.trim().match(/[ T](\d{2}):(\d{2})/);
  if (!match) return -1;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return -1;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

/**
 * Minutes past midnight for a BARE clock string — `"HH:MM"` or `"HH:MM:SS"`,
 * which is how the events endpoint sends `start_time` / `end_time` in its own
 * fields. `-1` when there is no time: the API uses `""` (and sometimes `null`)
 * for an all-day event, and `-1` is what the rest of this module already reads
 * as "no time part".
 *
 * Distinct from {@link civilMinutePart}, which wants the time to be preceded by
 * a date (`"YYYY-MM-DD HH:MM"`) and so returns `-1` for a bare clock. Both are
 * needed because the events payload carries the time in BOTH shapes depending
 * on the backend's age — the separate field on a current server, and embedded
 * in `start_date` on an older one.
 *
 * Read as a WALL CLOCK, like everything else this API stores: the value has no
 * zone, so there is nothing to convert from and converting would move it.
 */
export function clockMinutePart(value: unknown): number {
  if (typeof value !== 'string') return -1;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return -1;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return -1;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

/**
 * Local midnight (ms) for a civil date, or `0` when it is unparseable.
 *
 * Built field by field rather than through `new Date(string)`, which the spec
 * parses as UTC for a date-only value. Impossible dates ('2026-02-31') roll
 * over in the `Date` constructor, so the result is checked back before it is
 * trusted.
 */
export function civilMs(date: string): number {
  if (!DATE_ONLY_RE.test(date)) return 0;
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  const local = new Date(year, month - 1, day);
  if (local.getFullYear() !== year || local.getMonth() !== month - 1 || local.getDate() !== day) {
    return 0;
  }
  return local.getTime();
}

/** `YYYY-MM-DD` for a local `Date` — its LOCAL day, never the UTC one. */
export function civilKeyOf(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * `date` shifted by `days` civil days. Stepped through a local `Date` so a DST
 * transition can't drop or duplicate a day the way adding 86 400 000 ms would.
 */
export function addCivilDays(date: string, days: number): string {
  const ms = civilMs(date);
  if (!ms) return date;
  const shifted = new Date(ms);
  shifted.setDate(shifted.getDate() + days);
  return civilKeyOf(shifted);
}

/** A half-open civil window: `startDate` inclusive, `endDateExclusive` exclusive. */
export interface CalendarPeriod {
  startDate: string;
  endDateExclusive: string;
}

/** The inclusive last day of a half-open period — what the API's `end_date` wants. */
export function inclusiveEndOf(period: CalendarPeriod): string {
  return addCivilDays(period.endDateExclusive, -1);
}

// ── The canonical entry ──────────────────────────────────────────────────────

/** Which authoritative ERPat source an entry came from. */
export type CalendarSource = 'holiday' | 'leave' | 'advisory' | 'event';

/**
 * The colour/emphasis family for a status pill. Never the ONLY carrier of the
 * status — every entry also renders `statusLabel` as text (WCAG 1.4.1).
 */
export type EntryTone = 'brand' | 'positive' | 'warning' | 'danger' | 'neutral';

interface CalendarEntryBase {
  /**
   * Stable identity: `kind:recordId:occurrenceId`. A recurring event cannot be
   * keyed on its master id alone — several of its occurrences are visible at
   * once — so the occurrence (or, failing that, its start date) rides along.
   */
  key: string;
  sourceRecordId: string;
  occurrenceId: string;
  title: string;
  /** Plain-text summary. Never carries a leave reason (see `mapLeaveEntry`). */
  summary: string;

  /** Inclusive first civil day, `YYYY-MM-DD`. */
  startDate: string;
  /** EXCLUSIVE last civil day, `YYYY-MM-DD` — always at least start + 1 day. */
  endDateExclusive: string;
  /** Local midnight of `startDate`; the overlap and sort axis. */
  startMs: number;
  /** Local midnight of `endDateExclusive`. */
  endExclusiveMs: number;
  /** Days the entry covers (≥ 1). */
  spanDays: number;

  /**
   * `all_day` or `timed` — never both. Only events can be timed; the other
   * three sources name a day and nothing finer.
   */
  timing: 'all_day' | 'timed';
  /** e.g. "2:00 PM – 4:00 PM", or '' for an all-day entry. */
  timeLabel: string;
  /** Minutes past midnight for a timed entry; `-1` for all-day (sorts first). */
  sortMinute: number;
  /** e.g. "Mon, Sep 14, 2026" or "Sep 14 – Sep 16, 2026". */
  dateRangeLabel: string;

  /**
   * Does this apply to the signed-in employee? `null` means THE SOURCE CANNOT
   * SAY — which is the honest answer for an establishment-scoped holiday, since
   * the employee's establishment is not exposed to this app. The UI must render
   * that as "scope unknown", never as a silent yes.
   */
  appliesToMe: boolean | null;
  /** Safe scope labels, e.g. ["Company-wide"]. Never a recipient list. */
  scopeLabels: string[];
  /** Plain-language "why you're seeing this" — safe explanations only. */
  whyShown: string;

  /** The entry's state, as TEXT. Always rendered; colour is decoration. */
  statusLabel: string;
  statusTone: EntryTone;
  /** Set when the record has been revised, e.g. "Updated Sep 10, 2026". */
  updatedLabel: string;

  /** In-app route to the authoritative record's page. */
  sourceRoute: string;
  sourceRouteLabel: string;

  /** Extra label/value pairs for the detail sheet. */
  detailRows: { label: string; value: string }[];
}

export interface HolidayEntry extends CalendarEntryBase {
  kind: 'holiday';
  holidayType: 'regular' | 'special' | 'unofficial' | 'other';
  /** The tenant's own `special_rule`, echoed — never re-derived into policy. */
  specialRule: string;
}

export interface LeaveEntry extends CalendarEntryBase {
  kind: 'leave';
  leaveStatus: LeaveStatusVariant;
  /** The server's authoritative day total; `0` when it sent none. */
  totalDays: number;
}

export interface AdvisoryEntry extends CalendarEntryBase {
  kind: 'advisory';
  unread: boolean;
  pinned: boolean;
  /** When the advisory was published — kept DISTINCT from its effective window. */
  publishedLabel: string;
}

export interface EventEntry extends CalendarEntryBase {
  kind: 'event';
  /** The API's own vocabulary. Displayed as Going / Not going / No response. */
  response: 'none' | 'confirmed' | 'rejected';
  location: string;
  organizer: string;
  /** True when the row came from a recurring series. */
  recurring: boolean;
}

export type CalendarEntry = HolidayEntry | LeaveEntry | AdvisoryEntry | EventEntry;

/** Display order when several entries share a day and a start time. */
export const SOURCE_ORDER: Record<CalendarSource, number> = {
  holiday: 0,
  advisory: 1,
  leave: 2,
  event: 3,
};

export const SOURCE_LABELS: Record<CalendarSource, string> = {
  holiday: 'Holidays',
  leave: 'My Leave',
  advisory: 'Advisories',
  event: 'Events',
};

/**
 * Nouns used when an accessible name counts a day's entries.
 *
 * Both forms are spelled out rather than derived by appending an "s": "advisory"
 * pluralises to "advisories", and "leave" does not pluralise at all. A screen
 * reader says whatever is written here, so "2 advisorys" is a defect a sighted
 * reviewer would never see.
 */
export const SOURCE_NOUNS: Record<CalendarSource, { one: string; many: string }> = {
  holiday: { one: 'holiday', many: 'holidays' },
  leave: { one: 'leave', many: 'leave' },
  advisory: { one: 'advisory', many: 'advisories' },
  event: { one: 'event', many: 'events' },
};

/** e.g. "1 advisory" / "2 advisories". */
export function countNoun(kind: CalendarSource, count: number): string {
  const noun = SOURCE_NOUNS[kind];
  return `${count} ${count === 1 ? noun.one : noun.many}`;
}

// ── Shared formatting ────────────────────────────────────────────────────────

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const value = pick<unknown>(rec, keys, fallback);
  return value == null ? fallback : String(value).trim();
};

const bool = (rec: Json, keys: string[]): boolean => {
  const value = pick<unknown>(rec, keys, false);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
  return false;
};

const num = (rec: Json, keys: string[]): number => {
  const value = Number(pick<unknown>(rec, keys, 0));
  return Number.isFinite(value) ? value : 0;
};

/** A civil date as a local `Date`, or null — the only way these become Dates. */
function civilDate(date: string): Date | null {
  const ms = civilMs(date);
  return ms ? new Date(ms) : null;
}

/** e.g. "Mon, Sep 14, 2026". */
export function civilDateLabel(date: string): string {
  const parsed = civilDate(date);
  return parsed
    ? parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : '';
}

/** e.g. "Mon, Sep 14, 2026" for one day, "Sep 14 – Sep 16, 2026" for a span. */
function rangeLabel(startDate: string, endDateExclusive: string): string {
  const lastDay = addCivilDays(endDateExclusive, -1);
  if (!startDate) return '';
  if (startDate === lastDay) return civilDateLabel(startDate);

  const start = civilDate(startDate);
  const end = civilDate(lastDay);
  if (!start || !end) return civilDateLabel(startDate);

  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString('en-US',
    sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
  const endText = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startText} – ${endText}`;
}

/** "2:00 PM" from minutes past midnight. */
function clockLabel(minute: number): string {
  if (minute < 0) return '';
  const at = new Date(2000, 0, 1, Math.floor(minute / 60), minute % 60);
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Whole civil days between two dates (`endExclusive - start`), floored at 1. */
function spanOf(startMs: number, endExclusiveMs: number): number {
  if (!startMs || !endExclusiveMs) return 1;
  return Math.max(1, Math.round((endExclusiveMs - startMs) / 86_400_000));
}

/**
 * Fill in the derived half of an entry (ms axis, span, labels) from its civil
 * dates, so no mapper has to remember to compute them consistently.
 */
function withDerived<T extends CalendarSource>(
  kind: T,
  recordId: string,
  occurrenceId: string,
  startDate: string,
  endDateExclusive: string,
  timing: 'all_day' | 'timed',
  sortMinute: number,
  timeLabel: string,
): Pick<
  CalendarEntryBase,
  | 'key' | 'sourceRecordId' | 'occurrenceId' | 'startDate' | 'endDateExclusive'
  | 'startMs' | 'endExclusiveMs' | 'spanDays' | 'timing' | 'timeLabel'
  | 'sortMinute' | 'dateRangeLabel'
> {
  const startMs = civilMs(startDate);
  const endExclusiveMs = civilMs(endDateExclusive);
  return {
    key: `${kind}:${recordId}:${occurrenceId}`,
    sourceRecordId: recordId,
    occurrenceId,
    startDate,
    endDateExclusive,
    startMs,
    endExclusiveMs,
    spanDays: spanOf(startMs, endExclusiveMs),
    timing,
    timeLabel,
    sortMinute,
    dateRangeLabel: rangeLabel(startDate, endDateExclusive),
  };
}

// ── Holidays → HolidayEntry ──────────────────────────────────────────────────

const HOLIDAY_TYPE_LABELS: Record<HolidayEntry['holidayType'], string> = {
  regular: 'Regular holiday',
  special: 'Special holiday',
  unofficial: 'Unofficial observance',
  other: 'Holiday',
};

/**
 * A readable form of the tenant's `special_rule`. The rule is ECHOED, never
 * turned into a pay or attendance outcome — that is ERPat's call, and a value
 * like "200%" means nothing this app is entitled to interpret.
 */
function holidayRuleLabel(rule: string): string {
  if (!rule) return '';
  const known: Record<string, string> = {
    required_to_work: 'Required to work',
    no_business_operation: 'No business operation',
    paid_no_operation: 'Paid, no operation',
  };
  return known[rule.toLowerCase()] ?? rule.replace(/_/g, ' ');
}

export function mapHolidayEntry(raw: unknown): HolidayEntry | null {
  const record = asRecord(raw);
  const startDate = civilDateOf(pick<unknown>(record, ['date_from', 'date', 'start_date'], ''));
  if (!startDate) return null;

  const lastDay = civilDateOf(pick<unknown>(record, ['date_to', 'date', 'end_date'], '')) || startDate;
  // A `date_to` before `date_from` is malformed; collapse to a single day
  // rather than emitting a negative span the grid can't place.
  const endDateExclusive = addCivilDays(civilMs(lastDay) >= civilMs(startDate) ? lastDay : startDate, 1);

  const rawType = str(record, ['type']).toLowerCase();
  const holidayType: HolidayEntry['holidayType'] =
    rawType === 'regular' || rawType === 'special' || rawType === 'unofficial' ? rawType : 'other';

  const establishment = asRecord(pick<unknown>(record, ['establishment'], {}));
  const names = Array.isArray(establishment.names)
    ? establishment.names.map((n) => (typeof n === 'string' ? n.trim() : '')).filter(Boolean)
    : [];
  const companyWide = bool(record, ['is_all_establishments']);
  const rule = holidayRuleLabel(str(record, ['special_rule']));

  // `appliesToMe` is TRUE only for a company-wide holiday. An establishment
  // holiday stays `null`: this app is never told which establishment the
  // employee belongs to, so "applies to you" would be a guess — and a wrong one
  // is the difference between planning a day off and not.
  const appliesToMe = companyWide ? true : null;
  const scopeLabels = companyWide
    ? ['Company-wide']
    : names.length
      ? names
      : ['Selected establishments'];

  const id = str(record, ['id', 'uuid'], `${startDate}-${str(record, ['title'])}`);
  const detailRows: { label: string; value: string }[] = [];
  if (rule) detailRows.push({ label: 'Holiday rule', value: rule });
  detailRows.push({ label: 'Scope', value: scopeLabels.join(', ') });

  return {
    kind: 'holiday',
    ...withDerived('holiday', id, startDate, startDate, endDateExclusive, 'all_day', -1, ''),
    title: str(record, ['title', 'name'], 'Holiday'),
    summary: str(record, ['description']),
    holidayType,
    specialRule: rule,
    appliesToMe,
    scopeLabels,
    whyShown: companyWide
      ? 'Declared for the whole company.'
      : 'Declared for selected establishments — check with HR whether it covers yours.',
    statusLabel: HOLIDAY_TYPE_LABELS[holidayType],
    statusTone: holidayType === 'unofficial' ? 'neutral' : 'brand',
    updatedLabel: '',
    sourceRoute: '/holidays',
    sourceRouteLabel: 'Open in Holidays',
    detailRows,
  };
}

// ── Leave → LeaveEntry ───────────────────────────────────────────────────────

const LEAVE_STATUS_LABELS: Record<LeaveStatusVariant, string> = {
  approved: 'Approved',
  pending: 'Awaiting decision',
  // Endorsed by a first approver, waiting on a final one. Still undecided, so it reads as
  // a continuation of "awaiting" rather than a different kind of thing.
  for_review: 'Awaiting final approval',
  rejected: 'Not approved',
  cancelled: 'Cancelled by you',
};

const LEAVE_STATUS_TONES: Record<LeaveStatusVariant, EntryTone> = {
  approved: 'positive',
  pending: 'warning',
  // Shares `pending`'s tone: on a month grid the useful distinction is decided vs not,
  // and a fourth colour for a mid-workflow step would read as a new outcome.
  for_review: 'warning',
  rejected: 'danger',
  cancelled: 'neutral',
};

/**
 * PRIVACY: the reason, any attachment and any approver comment are read from
 * the payload and deliberately DROPPED here. They exist in the Leaves module,
 * behind its own workflow — Calendar summarises, and a summary that leaks a
 * medical reason into a day cell is a disclosure, not a convenience.
 */
export function mapLeaveEntry(raw: unknown, index = 0): LeaveEntry | null {
  const record = asRecord(raw);
  const startDate = civilDateOf(pick<unknown>(record, ['start_date'], ''));
  if (!startDate) return null;

  const lastDay = civilDateOf(pick<unknown>(record, ['end_date'], '')) || startDate;
  const endDateExclusive = addCivilDays(civilMs(lastDay) >= civilMs(startDate) ? lastDay : startDate, 1);

  const status = leaveStatusVariant(str(record, ['status']));
  const totalDays = num(record, ['total_days']);
  const totalHours = num(record, ['total_hours']);
  const id = str(record, ['id', 'uuid'], String(index));

  const detailRows: { label: string; value: string }[] = [];
  // The server's OWN totals, shown as it sent them. Calendar must never
  // recount leave days: weekends, holidays and half-days are policy, and the
  // stored total is the only figure that matches the employee's credits.
  if (totalDays) detailRows.push({ label: 'Total days', value: `${totalDays} ${totalDays === 1 ? 'day' : 'days'}` });
  if (totalHours) detailRows.push({ label: 'Total hours', value: `${totalHours}` });
  detailRows.push({ label: 'Filed', value: civilDateOf(pick<unknown>(record, ['created_at', 'date_created'], '')) || '—' });

  return {
    kind: 'leave',
    ...withDerived('leave', id, startDate, startDate, endDateExclusive, 'all_day', -1, ''),
    title: str(record, ['leave_type_title', 'leave_type', 'type'], 'Leave'),
    summary: '',
    leaveStatus: status,
    totalDays,
    appliesToMe: true,
    scopeLabels: ['Your application'],
    whyShown: 'This is your own leave application.',
    statusLabel: LEAVE_STATUS_LABELS[status],
    statusTone: LEAVE_STATUS_TONES[status],
    updatedLabel: '',
    sourceRoute: '/leaves',
    sourceRouteLabel: 'Open in Leave',
    detailRows,
  };
}

// ── Advisories → AdvisoryEntry ───────────────────────────────────────────────

/**
 * Advisories carry TWO dates that are routinely confused: when they were
 * published, and when they are in effect. The effective window drives the
 * calendar; the publication moment is kept beside it as its own fact.
 */
export function mapAdvisoryEntry(raw: unknown): AdvisoryEntry | null {
  const advisory = mapAdvisory(raw);
  // An advisory with no effective window still has to land somewhere, and the
  // day it was published is the only defensible placement.
  const startDate = civilDateOf(advisory.startAt) || civilDateOf(advisory.createdAt);
  if (!startDate) return null;

  const lastDay = civilDateOf(advisory.endAt) || startDate;
  const endDateExclusive = addCivilDays(civilMs(lastDay) >= civilMs(startDate) ? lastDay : startDate, 1);

  const detailRows: { label: string; value: string }[] = [];
  if (advisory.dateLabel) detailRows.push({ label: 'Published', value: advisory.dateLabel });
  detailRows.push({ label: 'In effect', value: rangeLabel(startDate, endDateExclusive) });
  if (advisory.createdByLabel) detailRows.push({ label: 'Posted by', value: advisory.createdByLabel });

  return {
    kind: 'advisory',
    ...withDerived('advisory', advisory.id, startDate, startDate, endDateExclusive, 'all_day', -1, ''),
    title: advisory.title || 'Advisory',
    summary: advisory.excerpt,
    unread: advisory.unread,
    pinned: advisory.pinned,
    publishedLabel: advisory.dateLabel,
    appliesToMe: true,
    scopeLabels: ['Addressed to you'],
    whyShown: 'Sent to you or to everyone in the company.',
    // The read state is only trustworthy when the server tracked it; otherwise
    // the entry says nothing rather than claiming an unread it can't know.
    statusLabel: advisory.readTracked ? (advisory.unread ? 'Unread' : 'Read') : 'Advisory',
    statusTone: advisory.readTracked && advisory.unread ? 'warning' : 'neutral',
    updatedLabel: advisory.updatedDateLabel ? `Updated ${advisory.updatedDateLabel}` : '',
    sourceRoute: `/announcements/${encodeURIComponent(advisory.id)}`,
    sourceRouteLabel: 'Read advisory',
    detailRows,
  };
}

// ── Events → EventEntry ──────────────────────────────────────────────────────

const EVENT_RESPONSE_LABELS: Record<EventEntry['response'], string> = {
  none: 'No response yet',
  confirmed: 'Going',
  // The API says `rejected`; an employee reads "Not going". Normalised here so
  // the two vocabularies never both reach the screen.
  rejected: 'Not going',
};

const EVENT_RESPONSE_TONES: Record<EventEntry['response'], EntryTone> = {
  none: 'neutral',
  confirmed: 'positive',
  rejected: 'danger',
};

function eventResponseOf(value: string): EventEntry['response'] {
  const text = value.toLowerCase();
  if (text.startsWith('confirm') || text === 'yes' || text === 'going' || text === 'accepted') return 'confirmed';
  if (text.startsWith('reject') || text.startsWith('declin') || text === 'no') return 'rejected';
  return 'none';
}

/**
 * Events are the only timed source, and the only one that sends its clock time
 * in two different places depending on how old the backend is:
 *
 *  - A CURRENT server sends the day in `start_date` (`"2026-06-20"`), the clock
 *    in its own `start_time` field (`"14:00:00"`, or `""` for an all-day
 *    event), an explicit `all_day` flag, and an `occurrence_date` naming the day
 *    THIS row falls on (a recurring series is expanded server-side, so several
 *    rows share one `id` and differ only by that date).
 *  - An OLDER one put the whole thing in `start_date`
 *    (`"2026-06-20 14:00:00"`) with no flag at all, so "all-day" had to be
 *    INFERRED from a midnight start.
 *
 * Both are read, and the flag decides when it is present. Only when it is
 * ABSENT does a midnight start still mean all-day: on a current server `00:00`
 * is a real midnight start, and treating it as all-day would hide a time the
 * organizer deliberately set.
 *
 * The stored time carries no zone. It is read as a wall clock in the device's
 * zone, which is right for the overwhelmingly common case (employee and tenant
 * in the same zone) and is the only option until the backend stores a per-event
 * IANA zone. The end date is treated as INCLUSIVE, matching every other date
 * this API sends; an exclusive midnight end would show one extra day rather
 * than dropping one, which is the safer way to be wrong.
 */
export function mapEventEntry(raw: unknown, index = 0): EventEntry | null {
  const record = asRecord(raw);
  const rawStart = pick<unknown>(record, ['start_date', 'start', 'start_at', 'starts_at'], '');
  const rawEnd = pick<unknown>(record, ['end_date', 'end', 'end_at', 'ends_at'], '');

  const startDate = civilDateOf(rawStart);
  if (!startDate) return null;

  const lastDay = civilDateOf(rawEnd) || startDate;
  const endDateExclusive = addCivilDays(civilMs(lastDay) >= civilMs(startDate) ? lastDay : startDate, 1);

  // The dedicated `start_time` / `end_time` fields first, then the legacy
  // datetime embedded in `start_date` — see the note above.
  const startTimeField = pick<unknown>(record, ['start_time'], '');
  const endTimeField = pick<unknown>(record, ['end_time'], '');
  const startFromField = clockMinutePart(startTimeField);
  const endFromField = clockMinutePart(endTimeField);
  const startMinute = startFromField >= 0 ? startFromField : civilMinutePart(rawStart);
  const endMinute = endFromField >= 0 ? endFromField : civilMinutePart(rawEnd);

  // "Declared" means the server SENT the flag, not that the flag is true. A
  // server that answers the question gets to answer it; one that doesn't falls
  // back to the midnight heuristic that is all an older payload supports.
  const allDayDeclared = ['all_day', 'is_all_day'].some(
    (key) => record[key] !== undefined && record[key] !== null,
  );
  const allDayFlag = bool(record, ['all_day', 'is_all_day']);
  const timed = !allDayFlag && (allDayDeclared ? startMinute >= 0 : startMinute > 0);

  let timeLabel = '';
  if (timed) {
    const from = clockLabel(startMinute);
    // Only pair an end time when it belongs to the same day; a multi-day timed
    // event's end would otherwise read as if it were today.
    const to = endMinute >= 0 && lastDay === startDate && endMinute !== startMinute ? clockLabel(endMinute) : '';
    timeLabel = to ? `${from} – ${to}` : from;
  }

  // The modern `rsvp.my_status` ("going" / "not_going") is authoritative; the
  // legacy top-level `my_response` ("confirmed" / "rejected") is the fallback.
  // Normalised here so only ONE vocabulary — this file's — leaves the mapper.
  const rsvpStatus = str(asRecord(pick<unknown>(record, ['rsvp'], {})), ['my_status']).toLowerCase();
  const response: EventEntry['response'] =
    rsvpStatus === 'going'
      ? 'confirmed'
      : rsvpStatus === 'not_going'
        ? 'rejected'
        : eventResponseOf(str(record, ['my_response', 'response']));

  // `venue` is the current field; `location` is its legacy alias and is still
  // sent, so it must not win when both are present and disagree.
  const location = str(record, ['venue', 'location', 'place']);
  const organizer = str(record, ['created_by_name', 'organizer_name', 'organizer', 'host']);
  const recurring = bool(record, ['recurring', 'is_recurring']);
  const id = str(record, ['id', 'uuid'], String(index));
  // A recurring series returns one row per occurrence with the SAME id, so the
  // occurrence date — the field the events API now sends for exactly this —
  // is what keeps them apart.
  const occurrenceDate = civilDateOf(pick<unknown>(record, ['occurrence_date'], '')) || startDate;
  const occurrenceId = str(record, ['occurrence_id', 'instance_id']) || occurrenceDate;

  const detailRows: { label: string; value: string }[] = [];
  if (timeLabel) detailRows.push({ label: 'Time', value: timeLabel });
  if (location) detailRows.push({ label: 'Location', value: location });
  if (organizer) detailRows.push({ label: 'Organized by', value: organizer });
  if (recurring) detailRows.push({ label: 'Repeats', value: 'Part of a recurring series' });

  return {
    kind: 'event',
    ...withDerived('event', id, occurrenceId, startDate, endDateExclusive, timed ? 'timed' : 'all_day',
      timed ? startMinute : -1, timeLabel),
    title: str(record, ['title', 'name', 'subject'], 'Event'),
    summary: str(record, ['description_text', 'description', 'details']),
    response,
    location,
    organizer,
    recurring,
    appliesToMe: true,
    scopeLabels: ['Shared with you'],
    whyShown: 'Shared with you or with a team you belong to.',
    statusLabel: EVENT_RESPONSE_LABELS[response],
    statusTone: EVENT_RESPONSE_TONES[response],
    updatedLabel: '',
    // Deep-link to THIS occurrence. Without `?d=` a recurring series opens on
    // whichever occurrence the detail endpoint considers first, which for a
    // weekly meeting is rarely the one the reader tapped.
    sourceRoute: `/events/${encodeURIComponent(id)}${recurring ? `?d=${occurrenceDate}` : ''}`,
    sourceRouteLabel: 'Open event',
    detailRows,
  };
}

// ── Window selection ─────────────────────────────────────────────────────────

/**
 * Does the entry touch the window at all?
 *
 * OVERLAP, not containment — `start < rangeEnd && end > rangeStart`. A
 * containment test ("starts inside the window") drops the very records an
 * employee most needs to see: a holiday shutdown or a leave that began before
 * the month opened and is still running through it.
 */
export function overlapsPeriod(entry: CalendarEntry, period: CalendarPeriod): boolean {
  const rangeStart = civilMs(period.startDate);
  const rangeEnd = civilMs(period.endDateExclusive);
  if (!rangeStart || !rangeEnd) return true;
  return entry.startMs < rangeEnd && entry.endExclusiveMs > rangeStart;
}

/** Does the entry cover this civil day? */
export function coversDay(entry: CalendarEntry, dayMs: number): boolean {
  return entry.startMs <= dayMs && entry.endExclusiveMs > dayMs;
}

/**
 * Chronological order: earliest day first, all-day entries ahead of timed ones
 * within a day, then a stable source/key tie-break so the list never reshuffles
 * between renders of the same data.
 */
export function compareEntries(a: CalendarEntry, b: CalendarEntry): number {
  if (a.startMs !== b.startMs) return a.startMs - b.startMs;
  return compareWithinDay(a, b);
}

/**
 * Order for entries already known to share a day — the selected day's agenda.
 *
 * Start date is deliberately NOT part of it. Every entry here covers the SAME
 * day, so sorting by when each one began would file a leave that started last
 * week above a holiday falling on this one, which is not the order a reader of
 * that day expects. All-day entries come first, then timed ones by their clock
 * time, then a stable source tie-break.
 */
export function compareWithinDay(a: CalendarEntry, b: CalendarEntry): number {
  if (a.sortMinute !== b.sortMinute) return a.sortMinute - b.sortMinute;
  if (SOURCE_ORDER[a.kind] !== SOURCE_ORDER[b.kind]) return SOURCE_ORDER[a.kind] - SOURCE_ORDER[b.kind];
  return a.key.localeCompare(b.key);
}
